import { definedMap } from "@glideapps/ts-necessities";
import type { AppDescriptionContext, ComponentBuilder, ComponentDescriptor, PropertyDescriptor } from ".";
import { RequiredKind } from ".";
import type { PrimitiveValue } from "../../common/components/computation-types";
import type { SerializedComponent } from "../../common/components/SerializedApp";
import { AppKind } from "../../common/components/SerializedApp";
import { asString } from "../../common/computation-model/data";
import type {
    ComponentKind,
    InputOutputTables,
    MutatingScreenKind,
    PropertyDescription,
    TableColumn,
    TableGlideType,
} from "../../common/description";
import { getPrimitiveColumns, getTableColumn } from "../../common/description";
import { getDocURL } from "../../common/docUrl";
import { parseNumber } from "../../common/Support";
import { WireComponentKind } from "../../wire/component-kind";
import { makeNumber } from "../builder-utils";
import { decomposeFormatFormula, ValueFormatKind } from "../formulas/formula-specifications";
import type { BaseFieldComponentDescription } from "./base-field";
import { BaseFieldComponentHandler, defaultFieldComponent } from "./base-field";
import { makeNumberPropertyDescriptor } from "./descriptor-utils";
import { ColumnPropertyFlag, ColumnPropertyHandler } from "./property-handlers";
import { PropertySection } from "./property-sections";
import type { WireInflationBackend, WireRowComponentHydrator } from "./wire";

const ComponentKindNumberField: ComponentKind = "number-field";

const propertyNamePropertyHandler = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.EditedInApp,
        ColumnPropertyFlag.DefaultCaption,
        ColumnPropertyFlag.Searchable,
    ],
    undefined,
    undefined,
    getPrimitiveColumns,
    "number",
    PropertySection.Data
);

interface NumberFieldComponentDescription extends BaseFieldComponentDescription {
    readonly minValue: PropertyDescription | undefined;
    readonly maxValue: PropertyDescription | undefined;
}

export function convertInForNumberField(input: PrimitiveValue): number | undefined {
    if (typeof input === "number") {
        return input;
    } else {
        return parseNumber(asString(input));
    }
}

export class NumberFieldComponentHandler extends BaseFieldComponentHandler<NumberFieldComponentDescription> {
    constructor() {
        super(ComponentKindNumberField, "number");
    }

    public needValidation(desc: NumberFieldComponentDescription): boolean {
        return super.needValidation(desc) || desc.minValue !== undefined || desc.maxValue !== undefined;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: NumberFieldComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;

        const minValue = definedMap(desc.minValue, v => makeNumber(builder, v, tables.input));
        const maxValue = definedMap(desc.maxValue, v => makeNumber(builder, v, tables.input));

        const propertyName = propertyNamePropertyHandler.getColumnName(desc);
        if (propertyName === undefined) return b.emptyUI;
        const column = getTableColumn(tables.output, propertyName);
        if (column === undefined) return b.emptyUI;

        let prefix: string | undefined;
        let suffix: string | undefined;
        const formatSpec = definedMap(column.displayFormula, decomposeFormatFormula);
        if (formatSpec?.kind === ValueFormatKind.Number) {
            if (formatSpec.unitAfterNumber) {
                suffix = formatSpec.unit;
            } else {
                prefix = formatSpec.unit;
            }
        }

        return this.fieldEntryForTableColumn(
            builder,
            desc,
            tables,
            outputContext,
            mutatingScreenKind,
            "number",
            propertyNamePropertyHandler,
            "NumberField",
            validFlag,
            undefined,
            {
                minValue,
                maxValue,
                prefix: definedMap(prefix, b.constant),
                suffix: definedMap(suffix, b.constant),
            }
        );
    }

    public getDescriptor(
        _desc: NumberFieldComponentDescription,
        _table: TableGlideType | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor {
        const properties: PropertyDescriptor[] = [];
        if (ccc.appKind === AppKind.App) {
            properties.push(
                makeNumberPropertyDescriptor(
                    "minValue",
                    "Min Value",
                    "0",
                    RequiredKind.NotRequiredDefaultMissing,
                    0,
                    mutatingScreenKind,
                    {
                        searchable: false,
                        emptyByDefault: true,
                    }
                ),
                makeNumberPropertyDescriptor(
                    "maxValue",
                    "Max Value",
                    "100",
                    RequiredKind.NotRequiredDefaultMissing,
                    100,
                    mutatingScreenKind,
                    {
                        searchable: false,
                        emptyByDefault: true,
                    }
                )
            );
        }
        return this.getFieldDescriptor(
            ccc,
            "Number Entry",
            "Edit a number",
            "Entry Fields",
            "componentNumberEntry",
            propertyNamePropertyHandler,
            false,
            getDocURL("numberField"),
            mutatingScreenKind,
            "Number",
            properties
        );
    }

    public makeValidationFlag(): SerializedComponent | undefined {
        return undefined;
    }

    public inflate(
        ib: WireInflationBackend,
        desc: NumberFieldComponentDescription
    ): WireRowComponentHydrator | undefined {
        return super.inflateComponent(ib, desc, (base, value, token) => ({
            ...base,
            kind: WireComponentKind.NumberField,
            value: { value: convertInForNumberField(value), onChangeToken: token },
        }));
    }

    public static defaultComponent(column: TableColumn): NumberFieldComponentDescription {
        return { ...defaultFieldComponent(column, ComponentKindNumberField), minValue: undefined, maxValue: undefined };
    }
}
